import { useEffect, useState } from "react";
import { MAX_KEYRING_KEYS, keypairFromSecret, publicKeyToNpub } from "@timecapsule/crypto";
import { api, type KeyRecord } from "../api";
import { link } from "../App";
import { KeyringLockedError, carryKeyring, addOldKeys, keyringLocked, oldKeys } from "../keyring";
import { carryAddressBook } from "../addressBookCarry";
import { fingerprint, prove } from "../keys";
import { errorText, useT } from "../i18n";
import { isKeyRequestCancelled, useSession } from "../session";
import { formatTime } from "../time";
import { useRefreshHandler } from "../Refresh";
import { KeyFlow } from "./Onboarding";
import { PhraseInput } from "./KeySetup";

/**
 * Account -> Keys (PLAN.md 4.16 K1): what the page offers depends on what the account and this
 * device hold. No active key: create one (or use a phrase you have). A key the device lacks: get
 * it here. A key the device has: its details, Reset key (K2) and the keyring (K4).
 */
export function Keys() {
  const t = useT();
  const { me, keyStatus, refresh } = useSession();
  const [keys, setKeys] = useState<KeyRecord[] | null>(null);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reload = () => api.keys().then(setKeys, (err) => setError(errorText(err)));
  useEffect(() => { void reload(); }, [me.pubkey]);
  useRefreshHandler(async () => setKeys(await api.keys()));

  return (
    <div className="account-page keys-page">
      <div className="page-head"><h1>{t.keys.title}</h1><p className="muted">{t.keys.note}</p></div>
      {error && <p className="error">{error}</p>}

      {me.pubkey === null && !resetting && (
        <section className="card">
          <KeyFlow mode="account" onDone={() => { void refresh(); void reload(); }} />
        </section>
      )}

      {me.pubkey !== null && (keyStatus === "none" || keyStatus === "mismatch") && (
        <section className="card">
          <h2>{t.keys.getTitle}</h2>
          <p>{t.keys.getText}</p>
          <a className="button primary" href="/restore" onClick={link("/restore")}>{t.keys.getButton}</a>
        </section>
      )}

      {me.pubkey !== null && (keyStatus === "locked" || keyStatus === "unlocked") && !resetting && (
        <>
          <section className="card">
            <h2>{t.keys.activeTitle}</h2>
            <p className="small">{t.keys.fingerprint} <code>{fingerprint(me.pubkey)}</code></p>
            <details className="small"><summary>{t.keys.fullKey}</summary><code className="select">{publicKeyToNpub(me.pubkey)}</code></details>
            <button onClick={() => setResetting(true)}>{t.keys.reset}</button>
            <p className="small muted">{t.keys.resetNote}</p>
          </section>
          <KeyringSection />
        </>
      )}

      {resetting && <ResetKey keys={keys ?? []} onFinished={() => { setResetting(false); void refresh(); void reload(); }} onCancel={() => setResetting(false)} />}

      {keys && keys.length > 0 && (
        <section className="card">
          <h2>{t.keys.listTitle}</h2>
          <ul className="rows small key-list">
            {keys.map((k) => (
              <li key={k.pubkey}>
                <code>{fingerprint(k.pubkey)}</code>
                <span className={k.revoked_at ? "tag warn-tag" : "tag"}>{k.revoked_at ? t.keys.state.revoked : k.retired_at ? t.keys.state.retired : t.keys.state.active}</span>
                <span className="muted">{t.keys.created(formatTime(k.registered_at * 1000))}</span>
                <span className="muted">{k.last_used_at ? t.keys.lastUsed(formatTime(k.last_used_at * 1000)) : t.keys.neverUsed}</span>
                {k.revoked_at && <span className="muted">{t.keys.revokedAt(formatTime(k.revoked_at * 1000))}{k.compromised_since ? t.keys.stolenSince(formatTime(k.compromised_since * 1000)) : ""}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/**
 * Reset key (PLAN.md 4.16 K2): the typed phrase proves the current key, the member says whether it
 * was stolen and since when, confirms it is them, and the key is revoked; then a new key with a new
 * phrase. The old key goes into the keyring unless the member keeps the phrase themselves (K4).
 */
function ResetKey({ keys, onFinished, onCancel }: { keys: KeyRecord[]; onFinished: () => void; onCancel: () => void }) {
  const t = useT();
  const { me, confirmIdentity, refresh } = useSession();
  const [oldSecret, setOldSecret] = useState<Uint8Array | null>(null);
  const [stolen, setStolen] = useState(false);
  const [since, setSince] = useState("");
  const [keepOld, setKeepOld] = useState(true);
  const [revoked, setRevoked] = useState<Uint8Array | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = !!oldSecret && keypairFromSecret(oldSecret).publicKey === me.pubkey;
  const active = keys.find((k) => k.pubkey === me.pubkey);

  async function revoke() {
    if (!oldSecret || !me.pubkey) return;
    setBusy(true);
    setError(null);
    try {
      // No date given: everything since the key became active is suspect.
      const compromisedSince = stolen ? (since ? Math.floor(new Date(since).getTime() / 1000) : active?.activated_at ?? null) : null;
      // A full keyring could not take the old key after the revocation: stop before it (PLAN.md 4.18 step 6).
      if (keepOld && (await oldKeys(oldSecret)).size >= MAX_KEYRING_KEYS) {
        setError(t.keys.keyringFull);
        return;
      }
      await confirmIdentity(t.reauth.purposes.revokeKey);
      await api.revokeKey(await prove(oldSecret, "revoke", [me.pubkey, compromisedSince === null ? "" : String(compromisedSince)]), compromisedSince);
      setRevoked(oldSecret);
      await refresh();
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  // The new key, kept while the keyring has not moved to it, so the move can be tried again.
  const [made, setMade] = useState<Uint8Array | null>(null);

  async function finished(newSecret: Uint8Array) {
    setMade(newSecret);
    setBusy(true);
    setError(null);
    try {
      await carryKeyring(revoked!, newSecret, keepOld);
      // The address book moves in the same retryable step (reviews/06-action-plan.md 2.6).
      await carryAddressBook(revoked!, newSecret);
    } catch (err) {
      setError(err instanceof KeyringLockedError ? t.keys.keyringLockedReset : errorText(err));
      return;
    } finally {
      setBusy(false);
    }
    onFinished();
  }

  if (revoked) {
    return (
      <section className="card">
        <p className="small">{t.keys.revokedNow}</p>
        {!made && <KeyFlow mode="reset" onDone={(secret) => void finished(secret)} />}
        {error && <p className="error small">{error}</p>}
        {made && error && (
          <div className="actions-row">
            <button className="primary" disabled={busy} onClick={() => void finished(made)}>{t.keys.retryKeyring}</button>
            <button disabled={busy} onClick={onFinished}>{t.keys.later}</button>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="card">
      <h2>{t.keys.resetTitle}</h2>
      <p>{t.keys.resetText}</p>
      <p className="small">{t.keys.typeCurrent}</p>
      <PhraseInput onKey={setOldSecret} disabled={busy} />
      {oldSecret && !matches && <p className="error small">{t.keys.notCurrent}</p>}
      <fieldset className="plain-fieldset">
        <label className="radio"><input type="radio" checked={!stolen} onChange={() => setStolen(false)} disabled={busy} /> {t.keys.notStolen}</label>
        <label className="radio"><input type="radio" checked={stolen} onChange={() => setStolen(true)} disabled={busy} /> {t.keys.wasStolen}</label>
        {stolen && (
          <label className="small">
            {t.keys.stolenSinceLabel}
            <input type="datetime-local" value={since} max={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)} onChange={(e) => setSince(e.target.value)} disabled={busy} />
          </label>
        )}
        {stolen && <p className="small muted">{t.keys.stolenNote}</p>}
      </fieldset>
      <label className="radio"><input type="checkbox" checked={keepOld} onChange={(e) => setKeepOld(e.target.checked)} disabled={busy} /> {t.keys.keepOld}</label>
      <p className="small muted">{keepOld ? t.keys.keepOldNote : t.keys.ownPhraseNote}</p>
      <div className="actions-row">
        <button className="primary" disabled={!matches || busy} onClick={() => void revoke()}>{t.keys.revokeButton}</button>
        <button disabled={busy} onClick={onCancel}>{t.common.cancel}</button>
      </div>
      {error && <p className="error small">{error}</p>}
    </section>
  );
}

/** The keyring (K4): which earlier keys it holds, and an earlier phrase to add. */
function KeyringSection() {
  const t = useT();
  const { me, withKey } = useSession();
  const [held, setHeld] = useState<string[] | null>(null);
  const [locked, setLocked] = useState(false);
  const [adding, setAdding] = useState<Uint8Array | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mine = me.keys;
  const addingPubkey = adding ? keypairFromSecret(adding).publicKey : null;
  const addable = !!addingPubkey && addingPubkey !== me.pubkey && mine.includes(addingPubkey);

  async function act(fn: (key: Uint8Array) => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await withKey(t.keys.keyringPurpose, fn);
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h2>{t.keys.keyringTitle}</h2>
      <p className="small muted">{t.keys.keyringNote}</p>
      {held === null ? (
        <button disabled={busy} onClick={() => void act(async (key) => { setHeld([...(await oldKeys(key)).keys()]); setLocked(await keyringLocked(key)); })}>{t.keys.showKeyring}</button>
      ) : locked ? (
        <p className="warn small">{t.keys.keyringLocked}</p>
      ) : held.length === 0 ? (
        <p className="small">{t.keys.keyringEmpty}</p>
      ) : (
        <ul className="rows small">{held.map((pk) => <li key={pk}><code>{fingerprint(pk)}</code></li>)}</ul>
      )}
      <details className="small">
        <summary>{t.keys.addOld}</summary>
        <PhraseInput onKey={setAdding} disabled={busy} />
        {adding && !addable && <p className="error small">{t.keys.notOldKey}</p>}
        <button disabled={!addable || busy} onClick={() => void act(async (key) => { await addOldKeys(key, [adding!]); await carryAddressBook(adding!, key); setHeld([...(await oldKeys(key)).keys()]); setLocked(await keyringLocked(key)); setAdding(null); })}>{t.keys.addOldButton}</button>
      </details>
      {error && <p className="error small">{error}</p>}
    </section>
  );
}
